import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { getDoctors, getDepartments } from '../services/doctorsApi';
import LocationSelector from '../components/LocationSelector';
import { getCoordinatesFromPincode, formatDistance } from '../utils/location';
import { DoctorCard, Icon, IconBox, PageHero, SectionHeader } from '../components/ui';

const SORT_OPTIONS = [
  { value: 'experience',  label: 'Experience'        },
  { value: 'fee-asc',     label: 'Fee: Low → High'    },
  { value: 'fee-desc',    label: 'Fee: High → Low'    },
  { value: 'distance',    label: 'Distance'          },
];

const formatNextAvailable = (mins) => {
  if (mins == null) return 'Check schedule';
  if (mins < 60) return `Online Consultation in next ${mins} mins`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0
    ? `Online Consultation in next ${h} hr${h > 1 ? 's' : ''}`
    : `Online Consultation in next ${h} hr${h > 1 ? 's' : ''} ${m} mins`;
};

/* ── Skeleton ──────────────────────────────────────────────────────── */
const SkeletonDoctor = () => (
  <div className="ds-doc">
    <div style={{ display: 'flex', gap: 14 }}>
      <div className="skeleton" style={{ width: 72, height: 72, borderRadius: 14, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="skeleton" style={{ width: '70%', height: 14, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: '50%', height: 11, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: '40%', height: 11 }} />
      </div>
    </div>
    <div className="skeleton" style={{ width: '85%', height: 12 }} />
    <div className="skeleton" style={{ width: '100%', height: 34, borderRadius: 10 }} />
  </div>
);
/* ── Main page ─────────────────────────────────────────────────────── */
const DoctorList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();
  const { user } = useAuth();

  const mode = searchParams.get('mode') === 'walk-in' ? 'walk-in' : 'online';
  const department = searchParams.get('department') || 'all';
  const queryParam = searchParams.get('q') || '';

  const [doctors, setDoctors]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error,   setError]         = useState(null);
  const [sort,    setSort]          = useState('experience');

  // Search by doctor name / department / qualification (client-side, like Pharmacy search)
  const [searchInput, setSearchInput] = useState(queryParam);
  const debounceRef = useRef(null);

  // Location state for walk-in mode
  const [userLocation, setUserLocation] = useState(null); // { latitude, longitude } or { pincode }
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [departments, setDepartments] = useState([]);

  // ── fetch departments once ──────────────────────────────────────
  useEffect(() => {
    getDepartments().then(setDepartments).catch(console.error);
  }, []);

  // ── debounce search input → URL `q` param ────────────────────────
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchInput.trim()) next.set('q', searchInput.trim());
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ── keep search box in sync if `q` changes externally (e.g. nav link, back/forward) ──
  useEffect(() => {
    setSearchInput(queryParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParam]);

  // ── show location selector for walk-in mode on first load ───────
  useEffect(() => {
    if (mode === 'walk-in' && !userLocation) {
      setShowLocationSelector(true);
    }
  }, [mode, userLocation]);

  // ── handle location change ──────────────────────────────────────
  const handleLocationChange = (location) => {
    if (location.type === 'coords') {
      setUserLocation({ latitude: location.latitude, longitude: location.longitude });
    } else if (location.type === 'pincode') {
      const coords = getCoordinatesFromPincode(location.pincode);
      if (coords) {
        setUserLocation(coords);
      } else {
        // Fallback: use pincode for filtering
        setUserLocation({ pincode: location.pincode });
      }
    }
    setShowLocationSelector(false);
  };

  // ── data fetch ───────────────────────────────────────────────────
  useEffect(() => {
    if (!tenant?.id) return;

    // For walk-in mode, wait for location
    if (mode === 'walk-in' && !userLocation) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const params = { mode, department };

    // Add location params for walk-in mode
    if (mode === 'walk-in' && userLocation) {
      if (userLocation.latitude && userLocation.longitude) {
        params.lat = userLocation.latitude;
        params.lon = userLocation.longitude;
      } else if (userLocation.pincode) {
        params.pincode = userLocation.pincode;
      }
    }

    getDoctors(params)
      .then((data) => { setDoctors(data); setLoading(false); })
      .catch(() => { setError('Could not load doctors.'); setLoading(false); });
  }, [tenant?.id, mode, department, userLocation]);

  // ── client-side search + sort ───────────────────────────────────
  const sorted = useMemo(() => {
    let arr = [...doctors];

    const q = queryParam.toLowerCase().trim();
    if (q) {
      arr = arr.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.departmentLabel || '').toLowerCase().includes(q) ||
          (d.qualifications  || '').toLowerCase().includes(q)
      );
    }

    if (sort === 'experience') arr.sort((a, b) => b.yearsExperience - a.yearsExperience);
    if (sort === 'fee-asc')    arr.sort((a, b) => a.fee - b.fee);
    if (sort === 'fee-desc')   arr.sort((a, b) => b.fee - a.fee);
    if (sort === 'distance')   arr.sort((a, b) => a.distanceKm - b.distanceKm);
    return arr;
  }, [doctors, sort, queryParam]);

  // ── url-state helpers ───────────────────────────────────────────
  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  // ── booking entry-point: force login if not authenticated ────────
  const handleBook = (doctor) => {
    const target = `/services/doctors/${doctor.id}/book?mode=${mode}`;
    if (!user) {
      navigate('/login', { state: { from: target } });
      return;
    }
    navigate(target);
  };

  const handleView = (doctor) => {
    navigate(`/services/doctors/${doctor.id}/profile?mode=${mode}`);
  };

  // Search button commits immediately instead of waiting for the debounce.
  const commitSearch = (e) => {
    if (e) e.preventDefault();
    clearTimeout(debounceRef.current);
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set('q', searchInput.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  if (!tenant) {
    return (
      <div className="page">
        <main className="page-main">
          <div className="breadcrumb">
            <button className="t-link" onClick={() => navigate('/')}>Home</button>
            <span>›</span>
            <span className="bc-current">Doctors</span>
          </div>
          <PageHero
            title="Consult a Doctor"
            icon={<Icon name="stethoscope" size={30} style={{ color: 'var(--ds-primary)' }} />}
            subtitle="Select a tenant from the top bar to see available doctors."
            art="doctor"
          />
        </main>
      </div>
    );
  }

  const heading = mode === 'online' ? 'Doctors Online' : 'Doctors Available Near You';
  const crumbLeaf = mode === 'online' ? 'Online Consultation' : 'Walk In';

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/services/online-consultation')}>Doctors</button>
          <span>›</span>
          <span className="bc-current">{crumbLeaf}</span>
        </div>

        <PageHero
          title="Consult a Doctor"
          icon={<Icon name="stethoscope" size={30} style={{ color: 'var(--ds-primary)' }} />}
          subtitle="Connect with certified doctors online or visit one near you."
          art="doctor"
        />

        {/* Consultation mode tabs (Online / Walk-in) + Second Opinion entry point */}
        <div className="ds-tabs fade-up-1" role="tablist">
          <button
            role="tab" aria-selected={mode === 'online'}
            className={`ds-tab${mode === 'online' ? ' is-active' : ''}`}
            onClick={() => updateParam('mode', 'online')}
          >
            <Icon name="video" size={15} /> Online Consultation
          </button>
          <button
            role="tab" aria-selected={mode === 'walk-in'}
            className={`ds-tab${mode === 'walk-in' ? ' is-active' : ''}`}
            onClick={() => updateParam('mode', 'walk-in')}
          >
            <Icon name="hospital" size={15} /> Doctor Visit
          </button>
          <button
            className="ds-tab"
            onClick={() => navigate('/services/book-appointment?mode=online&secondOpinion=true')}
          >
            <Icon name="shield" size={15} /> Second Opinion
          </button>
        </div>

        {/* Search + filters */}
        <form className="ds-filterbar fade-up-2" onSubmit={commitSearch} role="search">
          <div className="search-wrap">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by doctor name, specialty, or qualification…"
              aria-label="Search doctors"
            />
            {searchInput && (
              <button type="button" className="search-clear" onClick={() => setSearchInput('')} aria-label="Clear search">×</button>
            )}
          </div>

          <select
            className="select-filter"
            value={department}
            onChange={(e) => updateParam('department', e.target.value === 'all' ? null : e.target.value)}
            aria-label="Specialty"
          >
            {departments.length === 0 && <option value="all">All Departments</option>}
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>

          <select
            className="select-filter"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Sort: {o.label}</option>
            ))}
          </select>

          <button type="submit" className="ds-btn">Search</button>
        </form>

        {/* Location selector for walk-in mode */}
        {mode === 'walk-in' && showLocationSelector && (
          <div className="card fade-up-2" style={{ marginBottom: 24, padding: 20 }}>
            <h3 className="t-h3" style={{ marginBottom: 8 }}>Select Your Location</h3>
            <p className="t-body-sm" style={{ marginBottom: 16, color: '#6b7280' }}>
              We need your location to show nearby doctors. You can share your current location or enter a pincode.
            </p>
            <LocationSelector
              onLocationChange={handleLocationChange}
              initialPincode={searchParams.get('pincode')}
            />
          </div>
        )}

        {/* Location summary for walk-in mode */}
        {mode === 'walk-in' && userLocation && !showLocationSelector && (
          <div className="card fade-up-2" style={{ marginBottom: 24, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconBox tone="cyan" size="sm"><Icon name="mapPin" size={16} /></IconBox>
            <div style={{ flex: 1 }}>
              <div className="t-label" style={{ marginBottom: 2 }}>Showing doctors near you</div>
              <div className="t-meta">
                {userLocation.pincode
                  ? `Pincode: ${userLocation.pincode}`
                  : `Lat: ${userLocation.latitude.toFixed(4)}, Lon: ${userLocation.longitude.toFixed(4)}`}
              </div>
            </div>
            <button
              onClick={() => setShowLocationSelector(true)}
              className="ds-btn ds-btn--ghost ds-btn--sm"
            >
              Change
            </button>
          </div>
        )}

        {/* Specialties (department filter) */}
        {departments.length > 1 && (
          <section className="fade-up-2" style={{ marginBottom: 'var(--ds-section)' }}>
            <SectionHeader title="Specialties" />
            <div className="ds-scroller">
              {departments.map((d) => {
                const active = department === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    aria-pressed={active}
                    className={`ds-tile${active ? ' is-active' : ''}`}
                    onClick={() => updateParam('department', d.id === 'all' ? null : d.id)}
                  >
                    <IconBox size="lg" tone={active ? 'indigo' : 'violet'}>{d.icon}</IconBox>
                    {d.id === 'all' ? 'All' : d.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Results */}
        <SectionHeader
          title={heading}
          meta={!loading && !error && `${sorted.length} ${sorted.length === 1 ? 'doctor' : 'doctors'}`}
        />

        {error ? (
          <div className="error-state"><span>⚠️</span> {error}</div>
        ) : (
          <div className="ds-card-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonDoctor key={i} />)
              : sorted.length === 0
                ? (
                  <div className="no-results" style={{ gridColumn: '1 / -1' }}>
                    <div className="no-results-icon">🔍</div>
                    <p className="no-results-title">
                      {queryParam
                        ? `No doctors match "${queryParam}"`
                        : 'No doctors match your filters'}
                    </p>
                    <button
                      className="t-link t-link-noresult"
                      onClick={() => { setSearchInput(''); updateParam('department', null); }}
                    >
                      Clear all filters →
                    </button>
                  </div>
                )
                : sorted.map((doc, i) => (
                  <div key={doc.id} style={{ animation: `fadeUp .35s ${Math.min(i, 8) * 40}ms ease both` }}>
                    <DoctorCard
                      doctor={doc}
                      online={mode === 'online' && doc.availableOnline}
                      availability={mode === 'online' ? formatNextAvailable(doc.nextOnlineInMins) : null}
                      location={mode === 'walk-in'
                        ? [doc.clinic, doc.distanceKm != null ? `${formatDistance(doc.distanceKm)} away` : null].filter(Boolean).join(' · ')
                        : null}
                      onBook={() => handleBook(doc)}
                      onView={() => handleView(doc)}
                    />
                  </div>
                ))
            }
          </div>
        )}

      </main>
    </div>
  );
};

export default DoctorList;